import { clsx } from "clsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useReviewContentQuery } from "@/api/content-review";
import { apiUrl } from "@/components/automate/InteractionLog";
import { LoaderCircle, X } from "lucide-react";
import { LoadingButton } from "@/components/LoadingButton";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { TweetImage } from "@/components/content-creation/TweetImage";

export function TweetImageSelector({
  id,
  className,
  personaId,
  value,
  onChange,
}: {
  id?: string;
  className?: string;
  personaId: string;
  value: string[];
  onChange: (imageIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          id={id}
          className={clsx(
            "w-full flex justify-center items-center border border-dashed rounded-md",
            className,
          )}
        >
          {value.length > 0 ? (
            <div className="flex flex-row justify-start gap-x-4 overflow-x-auto w-full px-4 py-4">
              {value.map((id) => (
                <div key={id} className="relative">
                  <Button
                    className="absolute top-1 right-1 w-5 h-5"
                    size="icon"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(value.filter((imageId) => imageId !== id));
                    }}
                  >
                    <X />
                  </Button>
                  <TweetImage id={id} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground/60 py-8">Select images</p>
          )}
        </button>
      </DialogTrigger>
      <ImageGalleryPicker
        // Reset state when the dialog is open
        key={String(open)}
        personaId={personaId}
        value={value}
        onSelect={(imageIds) => {
          setOpen(false);
          onChange(imageIds);
        }}
      />
    </Dialog>
  );
}

const MAX_IMAGES = 4;

function ImageGalleryPicker({
  personaId,
  value,
  onSelect,
}: {
  personaId: string;
  value: string[];
  onSelect: (imageIds: string[]) => void;
}) {
  const {
    isFetching: isContentLoading,
    data,
    fetchNextPage,
    hasNextPage,
  } = useReviewContentQuery({ persona: personaId });

  const loadingFirstPage = (data?.pages.length ?? 0) === 0 && isContentLoading;
  const [selectedImages, setSelectedImages] = useState(
    new Set<string>(new Set(value)),
  );

  return (
    <DialogContent className="max-w-[90vw] max-h-[90vh] flex flex-col h-full">
      <DialogHeader>
        <DialogTitle>Select Images</DialogTitle>
        <DialogDescription>
          Select images to add to your tweet. You can select up to four images.
        </DialogDescription>
      </DialogHeader>
      {loadingFirstPage ? (
        <div className="flex h-full justify-center items-center">
          <LoaderCircle className="m-8 h-4 w-4 animate-spin self" />
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Selected images: {selectedImages.size}/{MAX_IMAGES}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 py-4 overflow-y-auto basis-0 grow min-h-0">
            {data?.pages.flatMap((page) =>
              page.data.map((image) => (
                <button
                  className="relative h-fit"
                  disabled={
                    selectedImages.size >= MAX_IMAGES &&
                    !selectedImages.has(image.doc_id)
                  }
                  onClick={() =>
                    setSelectedImages((prev) => {
                      const next = new Set(prev);
                      if (prev.has(image.doc_id)) {
                        next.delete(image.doc_id);
                      } else {
                        next.add(image.doc_id);
                      }

                      return next;
                    })
                  }
                >
                  <img
                    key={image.doc_id}
                    className="w-full h-auto object-cover aspect-[4/3] rounded"
                    src={`${apiUrl}/get-file?url=${encodeURIComponent(image.thumbnail_url)}`}
                    alt={image.image_caption}
                  />
                  <div className="absolute top-1 left-1 z-10">
                    <Checkbox
                      className="rounded-full w-6 h-6"
                      checked={selectedImages.has(image.doc_id)}
                      disabled={
                        selectedImages.size >= MAX_IMAGES &&
                        !selectedImages.has(image.doc_id)
                      }
                    />
                  </div>
                </button>
              )),
            )}
            {hasNextPage ? (
              <div className="md:col-span-2 lg:col-span-4 flex justify-center">
                <LoadingButton
                  onClick={() => fetchNextPage()}
                  loading={isContentLoading}
                >
                  Load more
                </LoadingButton>
              </div>
            ) : null}
          </div>
        </>
      )}
      <DialogFooter>
        <Button
          type="submit"
          onClick={() => onSelect(Array.from(selectedImages))}
        >
          Select
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
